import { computeFitDistance, WALL_HEIGHT, WALL_WIDTH } from './sceneLighting.js'

export const THUMBNAIL_PX_PER_INCH = 5

export const CAMERA_MODE_STANDARD = 'standard'
export const CAMERA_MODE_WALL = 'wall'
export const CAMERA_MODE_FREE = 'free'

export const CAMERA_REFERENCE_ART_WIDTH = 50
export const CAMERA_REFERENCE_ART_HEIGHT = 50
export const CAMERA_STANDARD_PADDING = 1.08
export const CAMERA_WALL_PADDING = 1.02

/**
 * Padding when comparing the piece’s required fit distance to each framing preset
 * (matches prior ad-hoc 1.15 in FrameRenderer).
 */
export const AUTO_FRAMING_PIECE_PADDING = 1.15

/**
 * Framing presets in order from **tightest** to **loosest** camera.
 * Auto mode picks the tightest preset that still fits the piece; if none do, the last (loosest) wins.
 * Add a farther preset here (e.g. room / suite) when you add a matching `CAMERA_MODE_*` and UI.
 */
export const AUTO_FRAMING_PRESETS = [
  {
    mode: CAMERA_MODE_STANDARD,
    refWidth: CAMERA_REFERENCE_ART_WIDTH,
    refHeight: CAMERA_REFERENCE_ART_HEIGHT,
    padding: CAMERA_STANDARD_PADDING,
  },
  {
    mode: CAMERA_MODE_WALL,
    refWidth: WALL_WIDTH,
    refHeight: WALL_HEIGHT,
    padding: CAMERA_WALL_PADDING,
  },
]

export const FRAMING_PRESET_BY_MODE = new Map(
  AUTO_FRAMING_PRESETS.map((p) => [p.mode, p])
)

export function getCameraModeRank(mode) {
  return AUTO_FRAMING_PRESETS.findIndex((preset) => preset.mode === mode)
}

export function shouldPromoteCameraMode(currentMode, requestedMode) {
  if (currentMode == null) return true

  const currentRank = getCameraModeRank(currentMode)
  const requestedRank = getCameraModeRank(requestedMode)
  return currentRank !== -1 && requestedRank !== -1 && requestedRank > currentRank
}

export function autoFramingPresetDistance(preset, fov, aspect) {
  return computeFitDistance(preset.refWidth, preset.refHeight, fov, aspect, preset.padding)
}

/**
 * Auto framing: choose the tightest preset that fits a content rect within a viewport rect.
 * The caller decides what the content represents (scene, visible artwork, etc.) and what
 * the viewport represents (visible area, thumbnail viewport, etc.).
 */
export function selectAutoFramingMode({
  contentW,
  contentH,
  viewportW,
  viewportH,
  fov,
  piecePadding = AUTO_FRAMING_PIECE_PADDING,
  baselineViewportHeight = 900,
}) {
  const safeViewportW = Number.isFinite(viewportW) && viewportW > 0 ? viewportW : 1
  const safeViewportH = Number.isFinite(viewportH) && viewportH > 0 ? viewportH : baselineViewportHeight
  const aspect = safeViewportW / safeViewportH
  const requiredDist = computeFitDistance(contentW, contentH, fov, aspect, piecePadding)
  const heightScale = safeViewportH / baselineViewportHeight

  for (const preset of AUTO_FRAMING_PRESETS) {
    const availableDist = autoFramingPresetDistance(preset, fov, aspect) * heightScale
    if (requiredDist <= availableDist) return preset.mode
  }
  return AUTO_FRAMING_PRESETS[AUTO_FRAMING_PRESETS.length - 1].mode
}

export function toPositiveNumber(value, fallback = null) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return parsed
}

export function normalizeCameraMode(value) {
  const mode = String(value || '').trim().toLowerCase()
  if (mode === CAMERA_MODE_STANDARD || mode === CAMERA_MODE_WALL || mode === CAMERA_MODE_FREE) {
    return mode
  }
  return CAMERA_MODE_STANDARD
}
